import MuiLink, { LinkProps as MuiLinkProps } from "@mui/material/Link"
import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined"
import { observer } from "mobx-react-lite"
import { useTheme } from "theme/useTheme"

interface GapLinkProps extends MuiLinkProps {
   children: React.ReactNode
   /** Underline drawn under the label. Defaults to 'solid'. */
   underlineStyle?: 'solid' | 'dashed' | 'none'
   /** Render the label at fontWeight 700. */
   bold?: boolean
   /**
    * Treat as a link that leaves the app: adds rel="noopener noreferrer"
    * (on anchors) and an open_in_new icon. Implied when target === '_blank'.
    */
   external?: boolean
   /** When set, wrap the link in a MUI Tooltip with these props. */
   tooltip?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>
}

/**
 * The Gap's single branded text link. Supersedes the former GapUnderlineLink
 * and LinkText.
 *
 * - underlineStyle: 'solid' (default) | 'dashed' | 'none'
 * - bold: fontWeight 700
 * - external: rel="noopener noreferrer" + open_in_new icon (implied by target="_blank")
 * - tooltip: wrap the link in a Tooltip
 *
 * The underline colour is derived from the text colour via color-mix, so it
 * follows whatever `color`/`sx` colour the link renders with.
*/
export default observer(function GapLink({ children, underlineStyle = 'solid', bold, external, tooltip, ...linkProps }: GapLinkProps) {
   const theme = useTheme()
   const isExternal = external || linkProps.target === '_blank'
   const isAnchor = 'href' in linkProps || linkProps.component === 'a'
   const renderAsButton = !('href' in linkProps) && !linkProps.component
   const px = '.2rem'

   const resolvedProps = {
      component: linkProps.component ?? ('href' in linkProps ? 'a' : 'button'),
      ...(renderAsButton ? { type: linkProps.type ?? 'button' } : {}),
      ...(isExternal && isAnchor ? { rel: linkProps.rel ?? 'noopener noreferrer' } : {}),
   }

   const link = <MuiLink
      {...linkProps as any}
      {...resolvedProps}
      sx={{
         color: theme.palette.gap.primary['800'],
         cursor: 'pointer',
         textDecoration: 'none',
         verticalAlign: 'baseline',
         ...(bold ? { fontWeight: 700 } : {}),
         ...(isExternal ? { display: 'inline-flex', alignItems: 'center', gap: 0.5 } : {}),
         ...(linkProps.sx || {}),
      }}>
      <Box component="span" sx={underlineStyle === 'none' ? undefined : {
         position: 'relative',
         px,
         marginX: `-${px}`,
         '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '.13rem',
            left: px,
            right: px,
            borderBottom: `1px ${underlineStyle} color-mix(in srgb, currentColor 60%, transparent)`,
         },
      }}>
         {children}
      </Box>
      {isExternal ? <OpenInNewOutlined sx={{ fontSize: 16 }} /> : null}
   </MuiLink>

   return tooltip ? <Tooltip {...tooltip}>{link}</Tooltip> : link
})
